import React, { useState, useEffect, useRef } from "react";
import { View, StyleSheet, TextInput, Platform } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, { useSharedValue, useAnimatedStyle, withSpring, runOnJS } from "react-native-reanimated";
import { Button } from "@/components/ui/Button";
import { ThemedText } from "@/components/ThemedText";
import ContextMenu from "@/components/ContextMenu";
import { useTheme } from "@/hooks/useTheme";
import { parseDurationText, formatDuration } from "@/lib/dateTimeInput";
import { CompactModifierPicker } from "./CompactModifierPicker";
import { TIME_MODIFIERS, type TimeModifierCode } from "@/types/db/time_cards";
import type { SegmentRowProps, TimeSegment } from "./types";

// Approximate row height for calculating target index during drag
const ROW_HEIGHT = 44;

export function SegmentRow({
  segment,
  index,
  onUpdate,
  onDelete,
  onMenuToggle,
  onDragStart,
  onDragEnd,
  isDragging,
}: SegmentRowProps) {
  const { colors } = useTheme();

  // Local picker states
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [modifiersPickerOpen, setModifiersPickerOpen] = useState(false);

  // Refs for toggle buttons (to ignore in click-outside detection)
  const typeBtnRef = useRef<View>(null);
  const modifiersBtnRef = useRef<View>(null);

  // Track if any menu is open and notify parent
  const isAnyMenuOpen = typeMenuOpen || modifiersPickerOpen;

  useEffect(() => {
    onMenuToggle?.(isAnyMenuOpen);
  }, [isAnyMenuOpen, onMenuToggle]);

  // Drag animation values
  const translateY = useSharedValue(0);
  const scale = useSharedValue(1);
  const shadowOpacity = useSharedValue(0);

  // Pan gesture for drag handle
  const panGesture = Gesture.Pan()
    .onStart(() => {
      scale.value = withSpring(1.02);
      shadowOpacity.value = withSpring(0.15);
      if (onDragStart) {
        runOnJS(onDragStart)(index);
      }
    })
    .onUpdate((event) => {
      translateY.value = event.translationY;
    })
    .onEnd((event) => {
      const targetOffset = Math.round(event.translationY / ROW_HEIGHT);
      const toIndex = index + targetOffset;

      translateY.value = withSpring(0);
      scale.value = withSpring(1);
      shadowOpacity.value = withSpring(0);

      if (onDragEnd && targetOffset !== 0) {
        runOnJS(onDragEnd)(index, toIndex);
      }
    });

  // Animated styles for the row during drag (transform only, no zIndex)
  const animatedRowStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }, { scale: scale.value }],
    boxShadow: `0px 4px 8px rgba(0,0,0,${shadowOpacity.value})`,
  }));

  // Format display values
  // Duration shown/edited in ShiftOverview's "Hh MMm" format. Segment duration
  // is in hours; the shared parser/formatter work in minutes, so convert here.
  const durationDisplay = formatDuration(Math.round(segment.duration * 60));
  const [durationText, setDurationText] = useState(durationDisplay);
  useEffect(() => {
    setDurationText(durationDisplay);
  }, [durationDisplay]);

  const handleTypeChange = (newType: TimeSegment["type"]) => {
    // worked and non-worked are always payable
    const isAlwaysPayable = newType === "worked" || newType === "non-worked";
    onUpdate({ ...segment, type: newType, payable: isAlwaysPayable ? true : segment.payable });
    setTypeMenuOpen(false);
  };

  const handleBillableToggle = () => {
    onUpdate({ ...segment, billable: !segment.billable });
  };

  // worked and non-worked are always payable (non-editable)
  const isPayableLocked = segment.type === "worked" || segment.type === "non-worked";
  const isPayable = isPayableLocked ? true : segment.payable;

  const handlePayableToggle = () => {
    if (isPayableLocked) return;
    onUpdate({ ...segment, payable: !segment.payable });
  };

  const handleModifiersChange = (modifiers: TimeModifierCode[]) => {
    onUpdate({ ...segment, modifiers: modifiers.length > 0 ? modifiers : undefined });
  };

  const handleDurationChange = (newDurationHours: number) => {
    onUpdate({
      ...segment,
      duration: newDurationHours,
    });
  };

  // Commit natural duration text ("8h 30m", "45m", "1.5"). Parsed to minutes by
  // the shared parser, then stored back as hours; reverts on a bad parse.
  const commitDuration = (text: string) => {
    const minutes = parseDurationText(text, 1);
    if (minutes != null) {
      handleDurationChange(minutes / 60);
    } else {
      setDurationText(durationDisplay);
    }
  };

  // Type colors: worked=green, meal=amber, break=gray, non-worked=primary
  const workedColor = colors.complete?.border || "#10b981";
  const mealColor = colors.pending?.border || "#f59e0b";
  const breakColor = colors.neutral?.border || "#6b7280";
  const nonWorkedColor = colors.primary || "#3b82f6";

  const getTypeColor = (type: TimeSegment["type"]) => {
    switch (type) {
      case "worked":
        return workedColor;
      case "meal":
        return mealColor;
      case "break":
        return breakColor;
      case "non-worked":
        return nonWorkedColor;
    }
  };

  const typeColor = getTypeColor(segment.type);

  // Get icon for segment type
  const getTypeIcon = (type: TimeSegment["type"]) => {
    switch (type) {
      case "worked":
        return "briefcase";
      case "meal":
        return "food";
      case "break":
        return "coffee";
      case "non-worked":
        return "clock-outline";
    }
  };

  const typeIcon = getTypeIcon(segment.type);

  // Check if segment has modifiers
  const hasModifiers = segment.modifiers && segment.modifiers.length > 0;

  // Only set zIndex when needed: dragging or menu open (leave undefined otherwise for natural stacking)
  const needsElevation = isDragging || isAnyMenuOpen;
  const containerZIndex = isDragging ? 100 : isAnyMenuOpen ? 1 : undefined;

  return (
    <Animated.View
      style={[
        styles.container,
        { borderColor: colors.border, backgroundColor: colors.backgroundSecondary },
        needsElevation && { zIndex: containerZIndex, elevation: containerZIndex },
        animatedRowStyle,
        isDragging && styles.draggingContainer,
      ]}
    >
      {/* Main row */}
      <View style={styles.row}>
        {/* Drag handle (knurling pattern) */}
        <GestureDetector gesture={panGesture}>
          <Animated.View style={styles.dragHandle}>
            <View style={[styles.knurlDot, { backgroundColor: colors.textSecondary }]} />
            <View style={[styles.knurlDot, { backgroundColor: colors.textSecondary }]} />
            <View style={[styles.knurlDot, { backgroundColor: colors.textSecondary }]} />
            <View style={[styles.knurlDot, { backgroundColor: colors.textSecondary }]} />
            <View style={[styles.knurlDot, { backgroundColor: colors.textSecondary }]} />
            <View style={[styles.knurlDot, { backgroundColor: colors.textSecondary }]} />
          </Animated.View>
        </GestureDetector>

        {/* Billable checkbox */}
        <Button
          icon={segment.billable ? "checkbox-marked" : "checkbox-blank-outline"}
          iconSize={18}
          iconStyle={{ color: segment.billable ? colors.primary : colors.textSecondary }}
          style={styles.checkboxButton}
          onPress={handleBillableToggle}
        />

        {/* Payable checkbox */}
        <Button
          icon={isPayable ? "checkbox-marked" : "checkbox-blank-outline"}
          iconSize={18}
          iconStyle={{ color: isPayable ? colors.primary : colors.textSecondary }}
          style={[styles.checkboxButton, isPayableLocked && { opacity: 0.5 }]}
          onPress={handlePayableToggle}
        />

        {/* Type indicator and dropdown */}
        <View style={[styles.typeIndicator, { position: "relative" }, typeMenuOpen && { elevation: 1, zIndex: 1 }]}>
          <Button
            ref={typeBtnRef}
            icon={typeIcon}
            text={segment.type}
            style={[styles.typeButton, { backgroundColor: typeColor }]}
            textStyle={styles.typeButtonText}
            iconStyle={{ color: "white" }}
            iconSize={14}
            onPress={() => setTypeMenuOpen((prev) => !prev)}
          />
          <ContextMenu
            style={{ top: 36, left: -10, minWidth: 130, zIndex: 10 }}
            visible={typeMenuOpen}
            setVisible={setTypeMenuOpen}
            ignoreRef={typeBtnRef}
          >
            <Button
              icon="briefcase"
              text="worked"
              style={[styles.menuButton, segment.type === "worked" && { backgroundColor: workedColor }]}
              textStyle={[styles.menuButtonText, segment.type === "worked" && { color: "white" }]}
              iconStyle={segment.type === "worked" ? { color: "white" } : undefined}
              iconSize={14}
              onPress={() => handleTypeChange("worked")}
            />
            <Button
              icon="food"
              text="meal"
              style={[styles.menuButton, segment.type === "meal" && { backgroundColor: mealColor }]}
              textStyle={[styles.menuButtonText, segment.type === "meal" && { color: "white" }]}
              iconStyle={segment.type === "meal" ? { color: "white" } : undefined}
              iconSize={14}
              onPress={() => handleTypeChange("meal")}
            />
            <Button
              icon="coffee"
              text="break"
              style={[styles.menuButton, segment.type === "break" && { backgroundColor: breakColor }]}
              textStyle={[styles.menuButtonText, segment.type === "break" && { color: "white" }]}
              iconStyle={segment.type === "break" ? { color: "white" } : undefined}
              iconSize={14}
              onPress={() => handleTypeChange("break")}
            />
            <Button
              icon="clock-outline"
              text="non-worked"
              style={[styles.menuButton, segment.type === "non-worked" && { backgroundColor: nonWorkedColor }]}
              textStyle={[styles.menuButtonText, segment.type === "non-worked" && { color: "white" }]}
              iconStyle={segment.type === "non-worked" ? { color: "white" } : undefined}
              iconSize={14}
              onPress={() => handleTypeChange("non-worked")}
            />
          </ContextMenu>
        </View>

        {/* Duration — natural text input (no sub-menu) */}
        <View style={[styles.durationButton, styles.durationInputWrap, { borderColor: colors.border, flex: 1 }]}>
          <TextInput
            value={durationText}
            onChangeText={setDurationText}
            onBlur={() => commitDuration(durationText)}
            onSubmitEditing={() => commitDuration(durationText)}
            placeholder="e.g. 8h 30m"
            placeholderTextColor={colors.placeholder}
            style={[styles.durationInput, { color: colors.text }]}
          />
        </View>

        {/* Modifiers picker */}
        <View style={[{ position: "relative", flex: 1 }, modifiersPickerOpen && { elevation: 1, zIndex: 1 }]}>
          <Button
            ref={modifiersBtnRef}
            icon="plus-circle-outline"
            iconSize={16}
            text="Mod"
            style={[
              styles.modifiersButton,
              { borderColor: colors.border },
              hasModifiers && { borderColor: colors.primary, backgroundColor: `${colors.primary}10` },
            ]}
            textStyle={[{ fontSize: 10 }, hasModifiers && { color: colors.primary }]}
            iconStyle={hasModifiers ? { color: colors.primary } : undefined}
            onPress={() => setModifiersPickerOpen((prev) => !prev)}
          />
          <ContextMenu
            style={{ top: 36, right: 0, zIndex: 10 }}
            visible={modifiersPickerOpen}
            setVisible={setModifiersPickerOpen}
            ignoreRef={modifiersBtnRef}
          >
            <CompactModifierPicker
              selectedModifiers={segment.modifiers || []}
              onModifiersChange={handleModifiersChange}
            />
          </ContextMenu>
        </View>

        {/* Delete button */}
        <Button
          icon="trash-can-outline"
          iconSize={16}
          iconStyle={{ color: colors.attention?.text || "#ef4444" }}
          style={styles.deleteButton}
          onPress={() => onDelete(segment.id)}
        />
      </View>

      {/* Modifier chips row (only if modifiers exist) - zIndex -1 to stay under menus */}
      {hasModifiers && (
        <View style={[styles.modifierChipsRow, { zIndex: -1 }]}>
          {segment.modifiers!.map((code) => {
            const modifier = TIME_MODIFIERS[code];
            return (
              <View
                key={code}
                style={[styles.modifierChip, { backgroundColor: `${modifier.color}20`, borderColor: modifier.color }]}
              >
                <View style={[styles.modifierChipDot, { backgroundColor: modifier.color }]} />
                <ThemedText style={[styles.modifierChipText, { color: modifier.color }]}>
                  {code} {modifier.multiplier}x
                </ThemedText>
              </View>
            );
          })}
        </View>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  draggingContainer: {
    elevation: 10,
    borderRadius: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  dragHandle: {
    width: 12,
    height: 20,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignContent: "center",
    gap: 2,
    opacity: 0.5,
  },
  knurlDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  typeIndicator: {
    minWidth: 113,
  },
  typeButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    minHeight: 28,
    gap: 4,
  },
  typeButtonText: {
    color: "white",
    fontSize: 11,
    fontWeight: "500",
    textTransform: "capitalize",
  },
  menuButton: {
    backgroundColor: "transparent",
    justifyContent: "flex-start",
    paddingHorizontal: 8,
    borderRadius: 4,
    gap: 6,
  },
  menuButtonText: {
    fontSize: 12,
    textTransform: "capitalize",
  },
  checkboxButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
    minWidth: 24,
    minHeight: 28,
    padding: 0,
  },
  durationButton: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 28,
    minWidth: 65,
    gap: 4,
  },
  durationInputWrap: {
    minHeight: 32,
    paddingVertical: 0,
    justifyContent: "center",
  },
  durationInput: {
    fontSize: 12,
    fontWeight: "600",
    paddingVertical: 0,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as const, height: "100%" as const } : {}),
  },
  modifiersButton: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 4,
    minHeight: 28,
    gap: 2,
  },
  deleteButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
    minWidth: 34,
    minHeight: 28,
    padding: 0,
  },
  modifierChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    paddingHorizontal: 8,
    paddingBottom: 6,
  },
  modifierChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
  },
  modifierChipDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  modifierChipText: {
    fontSize: 10,
    fontWeight: "600",
  },
});
